import cn from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import { Link } from 'react-router';

import {
  DELIVERY_INFORMATION_MISSING,
  DELIVERY_OPTION,
  DROP_PRODUCT_NOT_AVAILABLE,
  DROP_PRODUCT_PURCHASE_AUTHORIZATION_MISSING,
  DROP_PRODUCT_QUANTITY_LIMIT,
  FULLFILMENT_NETWORK_MISSING,
  INVALID_SELECTED_DELIVERY_OPTION_FOR_LINE_ITEM,
  ITEM_QUANTITY_UNAVAILABLE,
  JURISDICTION,
  MISSING_DIGITAL_DELIVERY_INFORMATION,
  OFFER_LISTING_AND_OFFER_SKU_DIFFER,
  OFFER_LISTING_NOT_AVAILABLE_CONSTRAINT_VIOLATION,
  QUANTITY_LIMITS,
  SHIPPING_ENGINE_FILTER_BASED,
  SHIPPING_ENGINE_PROVIDER_BASED,
  SHIPPING_ENGINE_REMOVER_BASED,
  SHIPPING_ENGINE_SHIPPING_OFFERING_NOT_SET
} from 'constants/constraintViolations';

import { toUSD } from 'helpers/NumberFormats';
import { IS_GIFT_CARD } from 'common/regex';
import { makeOptions } from 'components/cart/CartQuantityDropdown';
import FinalSale from 'zen/components/common/FinalSale';
import css from 'styles/components/checkout/melody/review/retailItem.scss';

const RetailItem = (params, { testId = f => f, marketplace }) => {
  const {
    allowGiftOptions,
    allowMoveToFavorites,
    asinErrors,
    eligibleForGiftWrap,
    isFetchingCheckoutData,
    isLast,
    isReviewStep,
    onChangeQuantity,
    onMoveToFavoritesClick,
    onViewGiftOptionsClick = f => f,
    product: {
      asin,
      color,
      displaySize,
      finalSale,
      image: { url: imageUrl, alt: imageAlt },
      itemLengthDescription,
      lineItemId,
      onHand,
      price,
      originalPrice,
      quantity,
      shoeWidth,
      giftMessagable,
      giftMessage,
      hasGiftReceipt,
      giftWrapId,
      brandName,
      style
    }
  } = params;

  const { checkout: { asinVerbiage = 'Asin' } } = marketplace;
  const showMsrp = price < originalPrice;
  const mustBeRemovedFromPurchase = checkIfMustBeRemovedFromPurchase({ asinErrors, asin });

  return (
    <form method="post" action="/tbd" className={css.form}>
      <div className={cn(css.itemWrapper, { [css.last]: isLast, [css.updating]: isFetchingCheckoutData })} data-test-id={testId('retailShippableItemContainer')}>
        { isReviewStep && makeAsinErrors({ asinErrors, asin })}
        <div className={css.contentWrapper}>
          <div>
            <Link
              className={css.imageLink}
              to={`/p/asin/${asin}`}
              data-te="TE_CHECKOUT_REV_GO_TO_PDP"
              data-ted={asin}
              data-test-id={testId('productImage')}>
              <img width="80" src={imageUrl} alt={imageAlt} />
            </Link>
          </div>
          <div>
            <div>
              <div data-test-id={testId('productBrand')}>{brandName}</div>
              <div className={css.itemName} data-test-id={testId('productName')}>{style}</div>
              { !IS_GIFT_CARD.test(brandName) && <div data-test-id={testId('itemColor')}>Color: {color}</div> }
              { !!displaySize && <div data-test-id={testId('itemSize')}>Size: {displaySize}</div> }
              { !!shoeWidth && <div data-test-id={testId('itemWidth')}>Width: {shoeWidth}</div> }
              { !!itemLengthDescription && <div>Inseam: {itemLengthDescription}</div> }
              <div>{asinVerbiage}: {asin}</div>
              <div className={css.priceLine}>
                <span className={css.price} data-test-id={testId('itemPrice')}>{toUSD(price)}</span>
                { showMsrp && <span className={css.originalPrice}> MSRP: {toUSD(originalPrice)}</span> }
              </div>
              { finalSale && <FinalSale /> }
            </div>
            <div>
              {
                isReviewStep
                  ? mustBeRemovedFromPurchase
                    ? <button
                      type="button"
                      className={css.removeItemBtn}
                      data-test-id={testId('removeItemBtn')}
                      onClick={e => onChangeQuantity(e, lineItemId)}
                      value="0">Remove</button>
                    : <div className={css.formField}>
                      <label htmlFor={`qty-${lineItemId}`}>Quantity</label>
                      <select
                        data-test-id={testId('quantitySelector')}
                        disabled={isFetchingCheckoutData}
                        id={`qty-${lineItemId}`}
                        name={`qty-${lineItemId}`}
                        onChange={e => onChangeQuantity(e, lineItemId)}
                        value={quantity}>
                        <option value="0">Remove</option>
                        {makeOptions(onHand, quantity)}
                      </select>
                    </div>
                  : null
              }
            </div>

            { isReviewStep && makeGiftOptionsBtn({ allowGiftOptions, eligibleForGiftWrap, lineItemId, giftMessagable, giftMessage, hasGiftReceipt, giftWrapId, onViewGiftOptionsClick }) }

            {
              isReviewStep && allowMoveToFavorites && <div>
                <button
                  className={css.moveToFavs}
                  data-asin={asin}
                  data-line-item-id={lineItemId}
                  onClick={onMoveToFavoritesClick}
                  type="button">Move to <span>Favorites</span></button>
              </div>
            }
          </div>
        </div>
      </div>
    </form>
  );
};

export function makeGiftOptionsBtn(params) {
  const {
    allowGiftOptions,
    eligibleForGiftWrap,
    giftMessagable,
    giftMessage,
    giftWrapId,
    hasGiftReceipt,
    lineItemId,
    onViewGiftOptionsClick
  } = params;

  if (!allowGiftOptions || !eligibleForGiftWrap) {
    return null;
  }

  let message = '';

  switch (true) {
    case hasGiftReceipt && !!giftWrapId:
      message = 'Gift Receipt and Gift Bag/Box selected';
      break;
    case hasGiftReceipt && !!giftMessage:
      message = 'Gift Receipt and Gift Message selected';
      break;
    case hasGiftReceipt:
      message = 'Gift Receipt selected';
      break;
  }

  if (!giftMessagable) {
    return <div>
      <span className={css.gift}></span>Gift options not available
    </div>;
  }

  if (message.length) {
    return (
      <div>
        <div>
          <span className={css.gift}></span>Gift options: {message}
        </div>
        <div>
          <button
            className={css.showGiftOptions}
            data-line-item-id={lineItemId}
            data-gift-message={giftMessage}
            data-gift-wrap-id={giftWrapId}
            onClick={onViewGiftOptionsClick}
            type="button">Change Gift Options <span>gift options</span></button>
        </div>
      </div>
    );
  }

  return (
    <button
      className={css.showGiftOptions}
      data-line-item-id={lineItemId}
      data-gift-message={giftMessage}
      data-gift-wrap-id={giftWrapId}
      onClick={onViewGiftOptionsClick}
      type="button">Make this a <span>gift</span></button>
  );
}

export function checkIfMustBeRemovedFromPurchase({ asinErrors, asin }) {
  if (!asinErrors?.hasOwnProperty(asin)) {
    return false;
  }

  const asinErrorList = [
    DELIVERY_OPTION,
    DELIVERY_INFORMATION_MISSING,
    SHIPPING_ENGINE_FILTER_BASED
  ];

  const result = asinErrors[asin].filter(info => asinErrorList.some(name => name === info.name));

  return !!result.length;
}

export function makeAsinErrors({ asinErrors, asin }) {
  if (!asinErrors?.hasOwnProperty(asin)) {
    return null;
  }

  const asinErrorList = [
    DELIVERY_OPTION,
    DELIVERY_INFORMATION_MISSING,
    DROP_PRODUCT_NOT_AVAILABLE,
    DROP_PRODUCT_PURCHASE_AUTHORIZATION_MISSING,
    DROP_PRODUCT_QUANTITY_LIMIT,
    FULLFILMENT_NETWORK_MISSING,
    ITEM_QUANTITY_UNAVAILABLE,
    JURISDICTION,
    MISSING_DIGITAL_DELIVERY_INFORMATION,
    OFFER_LISTING_AND_OFFER_SKU_DIFFER,
    OFFER_LISTING_NOT_AVAILABLE_CONSTRAINT_VIOLATION,
    QUANTITY_LIMITS,
    SHIPPING_ENGINE_FILTER_BASED,
    SHIPPING_ENGINE_REMOVER_BASED,
    SHIPPING_ENGINE_PROVIDER_BASED,
    SHIPPING_ENGINE_SHIPPING_OFFERING_NOT_SET,
    INVALID_SELECTED_DELIVERY_OPTION_FOR_LINE_ITEM
  ];

  const result = asinErrors[asin].filter(info => asinErrorList.some(name => name === info.name)).map(cv => {
    switch (cv.name) {
      case ITEM_QUANTITY_UNAVAILABLE: {
        const message = `${cv.message} Please remove one or more of this item. There ${cv.quantityAvailable > 1 ? 'are' : 'is'} ${cv.quantityAvailable} available in stock.`;
        return <p key={cv.name}>{message}</p>;
      }
      case INVALID_SELECTED_DELIVERY_OPTION_FOR_LINE_ITEM: {
        return <p key={cv.name}>
          <strong>Please select a different shipping speed.</strong>  Unfortunately, this item cannot be shipped with the chosen shipment speed.
        </p>;
      }
      case SHIPPING_ENGINE_FILTER_BASED:
      case DELIVERY_OPTION:
      case DELIVERY_INFORMATION_MISSING: {
        return <p key={cv.name}>
          <strong>Please remove this item.</strong> Unfortunately, this item has no valid shipping option. We are working on fixing this issue on our end.
        </p>;
      }
      default: {
        return <p key={cv.name}>{cv.message}</p>;
      }
    }
  });

  return result.length ? (
    <div className={css.errorMsg}>
      {result}
    </div>
  ) : '';
}

RetailItem.contextTypes = {
  testId: PropTypes.func,
  marketplace: PropTypes.object
};

export default RetailItem;
